import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { SearchField, StatusDot, EmptyState, pressable, IconCheck } from './ui';

const ExchangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ExchangeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
`;

const ExchangeCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 14px 16px;
  text-align: left;
  border-radius: var(--radius-md);
  border: 1px solid ${props => (props.$selected ? 'var(--accent)' : 'var(--separator)')};
  background: ${props => (props.$selected ? 'var(--accent-tint)' : 'var(--surface)')};
  box-shadow: ${props => (props.$selected ? 'inset 0 0 0 1px var(--accent)' : 'none')};
  cursor: pointer;
  ${pressable}

  &:hover {
    border-color: ${props => (props.$selected ? 'var(--accent)' : 'var(--separator-strong)')};
    background: ${props => (props.$selected ? 'var(--accent-tint)' : 'var(--surface-muted)')};
  }
`;

const ExchangeName = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: -0.012em;
  color: var(--text);
  padding-right: 20px;
`;

const ExchangeStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const pop = keyframes`
  from {
    opacity: 0;
    transform: scale(0.4);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const SelectedMark = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--accent-fill);
  color: var(--on-accent);
  animation: ${pop} 320ms var(--ease-out) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const exchanges = [
  { id: 'binance', name: 'Binance', icon: '🟡', status: 'active' },
  { id: 'okx', name: 'OKX', icon: '🟢', status: 'active' },
  { id: 'kucoin', name: 'KuCoin', icon: '🔵', status: 'active' },
  { id: 'gate', name: 'Gate.io', icon: '🟠', status: 'active' },
  { id: 'mexc', name: 'MEXC', icon: '🟣', status: 'active' },
  { id: 'htx', name: 'HTX', icon: '🔴', status: 'active' },
  { id: 'bybit', name: 'Bybit', icon: '🟤', status: 'active' },
  { id: 'bitget', name: 'Bitget', icon: '⚫', status: 'active' },
  { id: 'bitfinex', name: 'Bitfinex', icon: '⚪', status: 'active' },
  { id: 'aster', name: 'Aster', icon: '⭐', status: 'active' },
  { id: 'hyperliquid', name: 'Hyper Liquid', icon: '💎', status: 'active' },
  { id: 'cryptocom', name: 'Crypto.com', icon: '🔷', status: 'active' },
  { id: 'coinex', name: 'CoinEx', icon: '🟩', status: 'active' },
  { id: 'poloniex', name: 'Poloniex', icon: '🟦', status: 'active' },
  { id: 'xtcom', name: 'XT.COM', icon: '🔶', status: 'active' },
  { id: 'bitmart', name: 'BitMart', icon: '⬛', status: 'active' },
  { id: 'bingx', name: 'BingX', icon: '🔸', status: 'active' },
  { id: 'bithumb', name: 'Bithumb (Spot)', icon: '🟧', status: 'inactive' },
  { id: 'upbit', name: 'Upbit (Spot)', icon: '🟪', status: 'inactive' }
];

const defaultExchangeLocale = {
  searchPlaceholder: 'Search exchange',
  searchAria: 'Search exchange',
  statusActive: 'Active',
  statusInactive: 'Inactive',
  selectedTitle: 'Selected exchange:',
  selectedSubtitle: 'Ready to pick trading pairs',
  noResults: 'No exchanges match “{query}”',
};

function ExchangeSelector({ selectedExchange, onExchangeChange, locale = {} }) {
  const exchangeLocale = {
    ...defaultExchangeLocale,
    ...locale,
  };
  const [searchTerm, setSearchTerm] = useState('');

  const filteredExchanges = exchanges.filter(exchange =>
    exchange.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExchangeSelect = (exchange) => {
    onExchangeChange(exchange);
  };

  return (
    <ExchangeContainer>
      <SearchField
        id="exchange-search"
        placeholder={exchangeLocale.searchPlaceholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        aria-label={exchangeLocale.searchAria}
      />

      {filteredExchanges.length > 0 ? (
        <ExchangeGrid>
          {filteredExchanges.map(exchange => {
            const isSelected = selectedExchange?.id === exchange.id;
            return (
              <ExchangeCard
                key={exchange.id}
                type="button"
                aria-pressed={isSelected}
                $selected={isSelected}
                onClick={() => handleExchangeSelect(exchange)}
              >
                <ExchangeName>{exchange.name}</ExchangeName>
                <ExchangeStatus>
                  <StatusDot $active={exchange.status === 'active'} />
                  {exchange.status === 'active' ? exchangeLocale.statusActive : exchangeLocale.statusInactive}
                </ExchangeStatus>
                {isSelected && (
                  <SelectedMark>
                    <IconCheck size={12} strokeWidth={3} />
                  </SelectedMark>
                )}
              </ExchangeCard>
            );
          })}
        </ExchangeGrid>
      ) : (
        <EmptyState>
          <p>{exchangeLocale.noResults.replace('{query}', searchTerm)}</p>
        </EmptyState>
      )}
    </ExchangeContainer>
  );
}

export default ExchangeSelector;
